import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, Package, Bot, BarChart2, RefreshCw, Globe, 
  Monitor, Phone, Smartphone, DollarSign, TrendingUp, Zap, 
  Users, ChevronRight, X, Menu, Star, Check
} from 'lucide-react';
import Layout from '../../components/layout';

export default function ShopSenseAILanding() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Layout>

    <div className="min-h-screen font-sans text-gray-800 overflow-x-hidden">
      {/* Header */}
      <header className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? "py-3 bg-white shadow-md" : "py-5 bg-transparent"
      }`}>
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center">
            <div className={`text-2xl font-bold transition-colors duration-300 ${
              scrolled ? "text-indigo-900" : "text-white"
            }`}>
              Shop<span className="text-blue-500">Sense</span>.AI
            </div>
            
            <button 
              className={`md:hidden focus:outline-none transition-colors duration-300 ${
                scrolled ? "text-indigo-900" : "text-white"
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            
            <nav className="hidden md:flex space-x-8 items-center">
              <NavLink href="#features" label="Features" scrolled={scrolled} />
              <NavLink href="#channels" label="Channels" scrolled={scrolled} />
              <NavLink href="#benefits" label="Benefits" scrolled={scrolled} />
              {/* <NavLink href="#testimonials" label="Testimonials" scrolled={scrolled} /> */}
              <a href="/contact" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-all duration-300 hover:shadow-lg">
                Get Started
              </a>
            </nav>
          </div>
          
          {isMenuOpen && (
            <nav className="md:hidden py-4 bg-white absolute top-full left-0 right-0 shadow-md border-t border-gray-100">
              <div className="container mx-auto px-6 flex flex-col space-y-3">
                <MobileNavLink href="#features" label="Features" onClick={() => setIsMenuOpen(false)} />
                <MobileNavLink href="#channels" label="Channels" onClick={() => setIsMenuOpen(false)} />
                <MobileNavLink href="#benefits" label="Benefits" onClick={() => setIsMenuOpen(false)} />
                {/* <MobileNavLink href="#testimonials" label="Testimonials" onClick={() => setIsMenuOpen(false)} /> */}
                <a 
                  href="#contact" 
                  className="bg-blue-600 hover:bg-blue-700 text-white text-center font-medium px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg mt-3"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </a>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center pt-20 bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 text-white overflow-hidden relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -right-1/4 top-1/4 w-1/2 h-1/2 bg-blue-500 rounded-full opacity-20 blur-3xl"></div>
          <div className="absolute -left-1/4 -bottom-1/4 w-1/2 h-1/2 bg-indigo-700 rounded-full opacity-20 blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 py-12 z-10 relative">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-1/2 text-center lg:text-left mb-10 lg:mb-0">
              <span className="inline-block px-4 py-1 rounded-full bg-blue-800 bg-opacity-50 text-blue-200 text-sm font-medium mb-6">
                AI Chatbot Suite for Retail
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Transform E-Commerce with AI Conversations
              </h1>
              <p className="text-lg sm:text-xl text-blue-100 mb-10 max-w-xl mx-auto lg:mx-0">
                Integrated SaaS solution delivering seamless omnichannel shopping through Web, WhatsApp, and App chatbots.
              </p>
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <a href="/request-demo" className="bg-white text-blue-800 hover:bg-blue-50 font-bold px-8 py-3 rounded-lg transition transform hover:-translate-y-1 shadow-lg">
                  Request Demo
                </a>
                <a href="https://www.shopsense.ai/" className="border-2 border-white text-white hover:bg-white hover:bg-opacity-10 font-bold px-8 py-3 rounded-lg transition transform hover:-translate-y-1">
                  Learn More
                </a>
              </div>
            </div>
            
            <div className="lg:w-1/2 relative">
              <div className="relative z-10 rounded-2xl shadow-2xl">
                <img src="https://cdn.easternpeak.com/wp-content/uploads/2021/05/Ways-Artificial-Intelligence-Empowers-eCommerce.jpg" alt="ShopSense AI Interface" className="w-full rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <SectionHeader 
            title="Key Features"
            subtitle="Enhance customer engagement and streamline operations with our AI-powered chatbot suite"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-16">
            <FeatureCard 
              icon={<MessageCircle size={28} />} 
              title="Conversational Commerce" 
              description="AI-driven product recommendations and guided selling to boost conversions."
            />
            <FeatureCard 
              icon={<Package size={28} />} 
              title="Order Management" 
              description="Real-time tracking and automated returns for seamless post-purchase support."
            />
            <FeatureCard 
              icon={<Bot size={28} />} 
              title="24/7 AI Support" 
              description="Instant FAQ responses with live agent handover when needed."
            />
            <FeatureCard 
              icon={<BarChart2 size={28} />} 
              title="Analytics & Insights" 
              description="Customer behavior analytics and sentiment analysis for better engagement."
            />
            <FeatureCard 
              icon={<RefreshCw size={28} />} 
              title="Seamless Integrations" 
              description="Connects with Shopify, Magento, payment gateways, and CRM systems."
            />
            <FeatureCard 
              icon={<Globe size={28} />} 
              title="Multi-Language" 
              description="Personalized responses in multiple languages for global reach."
            />
          </div>
        </div>
      </section>

      {/* Channels Section */}
      <section id="channels" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <SectionHeader 
            title="Omnichannel Engagement"
            subtitle="Deliver consistent experiences across all customer touchpoints"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-16">
            <ChannelItem 
              icon={<Monitor size={24} />} 
              title="Web Bot" 
              description="Instant assistance on your website for product discovery and checkout."
            />
            <ChannelItem 
              icon={<Phone size={24} />} 
              title="WhatsApp Bot" 
              description="Personalized support and order updates via WhatsApp."
            />
            <ChannelItem 
              icon={<Smartphone size={24} />} 
              title="App Bot" 
              description="Real-time assistance and recommendations in your mobile app."
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 text-white">
        <div className="container mx-auto px-6">
          <SectionHeader 
            title="Business Advantages"
            subtitle="Drive growth and efficiency with ShopSense.AI"
            light={true}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            <BenefitCard 
              icon={<DollarSign size={24} />} 
              title="Boost Sales" 
              description="Increase conversions with personalized recommendations."
            />
            <BenefitCard 
              icon={<Users size={24} />} 
              title="Reduce Costs" 
              description="Automate support and minimize live agent dependency."
            />
            <BenefitCard 
              icon={<Zap size={24} />} 
              title="Enhance Engagement" 
              description="Instant, personalized responses across channels."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-600 text-white text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Revolutionize Your Retail?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto">
            Start delivering exceptional shopping experiences with ShopSense.AI today.
          </p>
          <a href="/contact" className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-8 py-4 rounded-lg transition transform hover:-translate-y-1 shadow-lg">
            Get Started Now
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
            <div className="mb-4 md:mb-0">
              © {new Date().getFullYear()} ShopSense.AI by ENS Enterprises. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <a href="/privacy-policy" className="hover:text-blue-400 transition">Privacy</a>
              <a href="/privacy-policy" className="hover:text-blue-400 transition">Terms</a>
              <a href="/contact" className="hover:text-blue-400 transition">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </Layout>

  );
}

// Reusable Components
function SectionHeader({ title, subtitle, light = false }) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${light ? 'text-white' : 'text-indigo-900'}`}>
        {title}
      </h2>
      <p className={`text-lg ${light ? 'text-blue-100' : 'text-gray-600'}`}>
        {subtitle}
      </p>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition duration-300">
      <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-indigo-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

function ChannelItem({ icon, title, description }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="bg-blue-100 text-blue-600 p-3 rounded-lg mb-4 inline-block">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-indigo-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

function BenefitCard({ icon, title, description }) {
  return (
    <div className="bg-white bg-opacity-10 rounded-xl p-6">
      <div className="bg-white bg-opacity-20 text-white p-3 rounded-lg inline-block mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-blue-100">{description}</p>
    </div>
  );
}

function NavLink({ href, label, scrolled }) {
  return (
    <a 
      href={href} 
      className={`transition-colors duration-300 font-medium ${
        scrolled ? "text-gray-700 hover:text-blue-600" : "text-white hover:text-blue-200"
      }`}
    >
      {label}
    </a>
  );
}

function MobileNavLink({ href, label, onClick }) {
  return (
    <a 
      href={href} 
      className="text-gray-800 hover:text-blue-600 py-2 flex justify-between items-center"
      onClick={onClick}
    >
      {label}
      <ChevronRight size={18} />
    </a>
  );
}